import { useState } from 'react';
import { MasterDocument } from '@util/firestore/master';

interface NotifyMeModalProps {
  master: MasterDocument;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function NotifyMeModal({
  master,
  isOpen,
  onClose,
  onSuccess,
}: NotifyMeModalProps) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');

    try {
      const res = await fetch('/api/master/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masterId: master.id,
          masterTitle: master.title,
          email,
          phone: phone || undefined,
        }),
      });

      if (!res.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setSubmitting(false);
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[1.2rem] p-[2.4rem] max-w-[48rem] w-full mx-[2rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-[1.6rem]">
          <h2 className="text-[2rem] font-extrabold text-black">
            Get notified when one lists.
          </h2>
          <button
            onClick={onClose}
            className="text-[2rem] text-gray-400 hover:text-black transition-colors cursor-pointer leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <p className="text-[1.4rem] text-brand-dark-gray leading-[1.5] mb-[2rem]">
          {/* GB #1401 final gate round fix (M2): was {master.brand} {master.model},
              neither of which is guaranteed present on the Algolia-sourced
              MasterDocument the search zero-state passes in (the field IS
              indexed, but not every doc has it populated). `title` is what
              every caller's surrounding card already displays and, for the
              search path, is the one field the render gate above this
              component already requires to be non-empty before rendering
              at all — see SearchEmptyState.tsx's `bestMatch.title` check. */}
          We&apos;ll text or email you the moment a {master.title} is listed
          on GearFocus.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-[1.6rem]">
          <div className="flex flex-col gap-[0.4rem]">
            <label
              htmlFor="notify-modal-email"
              className="text-[1.4rem] font-medium text-black"
            >
              Your email address
            </label>
            <input
              id="notify-modal-email"
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="text-[1.4rem] border border-gray-300 rounded-[0.8rem] px-[1.2rem] py-[0.8rem] focus:outline-hidden focus:ring-2 focus:ring-brand-secondary focus:border-transparent"
            />
          </div>

          <div className="flex flex-col gap-[0.4rem]">
            <label
              htmlFor="notify-modal-phone"
              className="text-[1.4rem] font-medium text-black"
            >
              Phone number (optional)
            </label>
            <input
              id="notify-modal-phone"
              type="tel"
              placeholder="(555) 555-5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="text-[1.4rem] border border-gray-300 rounded-[0.8rem] px-[1.2rem] py-[0.8rem] focus:outline-hidden focus:ring-2 focus:ring-brand-secondary focus:border-transparent"
            />
            <p className="text-[1.2rem] text-gray-500 leading-[1.5] mt-[0.4rem]">
              Get a text when this item becomes available. By entering your phone
              number, you agree to receive SMS alerts from Refocus Marketplace.
              Msg frequency varies. Msg &amp; data rates may apply. Reply DELETE
              to stop receiving messages. Consent is not required to purchase.
            </p>
          </div>

          {errorMessage && (
            <p className="text-[1.3rem] text-red-600">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="inline-block w-fit bg-black text-white font-bold rounded-[1rem] px-[2.4rem] py-[0.8rem] text-[1.4rem] text-center hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Submitting...' : 'Notify Me'}
          </button>
        </form>
      </div>
    </div>
  );
}
